import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useAppConfig from 'hooks/useAppConfig';
import PropTypes from 'prop-types';
import { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DialogEditSettingView from '../DialogEditSettingView';
import { thunkGetAppConfig } from 'app/appSlice';
import MenuActionDropdown from 'general/components/Others/MenuActionDropdown';

GeneralSettingView.propTypes = {
    showFullUI: PropTypes.bool,
    prefKey: PropTypes.string,
};

function GeneralSettingView({ showFullUI, prefKey }) {
    // MARK: ---- PROPERTIES ----
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const appConfig = useAppConfig();
    const [settingList, setSettingList] = useState([]);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        setSettingList([
            {
                key: 'showShareRatio',
                name: 'Ẩn tỷ lệ góp vốn của NĐT HTKD',
                value: appConfig.showShareRatio ? 'Có' : 'Không',
                onClick: () => {
                    handleEditSetting(
                        'showShareRatio',
                        'Ẩn tỷ lệ góp vốn của NĐT HTKD',
                    )
                },
            },
            {
                key: 'ticketMaxDistance',
                name: 'Khoảng cách hiển thị công việc bảo trì sửa chữa',
                value: !!appConfig.ticketMaxDistance ? Utils.formatNumber(appConfig.ticketMaxDistance) + ' m' : '',
                onClick: () => {
                    handleEditSetting(
                        'ticketMaxDistance',
                        'Khoảng cách hiển thị công việc bảo trì sửa chữa',
                        'Trong khoảng',
                        'm',
                        false,
                    )
                },
            },
            {
                key: 'autoMaintenanceTicketTime',
                name: 'Thời gian sinh phiếu bảo trì tự động',
                value: !!appConfig.autoMaintenanceTicketTime ? 'Trước ' + Utils.formatNumber(appConfig.autoMaintenanceTicketTime) + ' ngày' : '',
                onClick: () => {
                    handleEditSetting(
                        'autoMaintenanceTicketTime',
                        'Thời gian sinh phiếu bảo trì tự động trước',
                        'Trước',
                        'Ngày',
                        false,
                    )
                },
            },
            {
                key: 'notifyUnassignedAfter',
                name: 'Thời gian thông báo khi không có người nhận công việc bảo trì sửa chữa kể từ lúc phân phối',
                value: (
                    <>
                        {appConfig.notifyUnassignedAfter?.map((item, i) => (
                            <div key={i} className='py-2'>{item.name}: {item.value} phút</div>
                        ))}
                    </>
                ),
                onClick: () => {
                    handleEditSetting('notifyUnassignedAfter')
                },
            },
            {
                key: 'checkinMaxDistance',
                name: 'Khoảng cách sai lệch khi KTV check-in',
                value: !!appConfig.checkinMaxDistance ? Utils.formatNumber(appConfig.checkinMaxDistance) + ' m' : '',
                onClick: () => {
                    handleEditSetting(
                        'checkinMaxDistance',
                        'Khoảng cách sai lệch khi KTV check-in',
                        'Trong khoảng',
                        'm',
                        false,
                    )
                },
            },
            {
                key: 'checkinTimeBefore',
                name: 'Thời gian sớm nhất KTV có thể check-in',
                value: !!appConfig.checkinTimeBefore ? 'Trước ' + Utils.formatNumber(appConfig.checkinTimeBefore) + ' phút' : 'Không giới hạn',
                onClick: () => {
                    handleEditSetting(
                        'checkinTimeBefore',
                        'Thời gian sớm nhất KTV có thể check-in cho đến hạn',
                        'Trước',
                        'Phút',
                        false,
                        false,
                    )
                },
            },
            {
                key: 'checkinTimeAfter',
                name: 'Thời gian muộn nhất KTV có thể check-in',
                value: !!appConfig.checkinTimeAfter ? 'Sau ' + Utils.formatNumber(appConfig.checkinTimeAfter) + ' phút' : 'Không giới hạn',
                onClick: () => {
                    handleEditSetting(
                        'checkinTimeAfter',
                        'Thời gian muộn nhất KTV có thể check-in sau hạn',
                        'Sau',
                        'Phút',
                        false,
                        false,
                    )
                },
            },
            {
                key: 'notifyBeforeDeadline',
                name: 'Thời gian gửi thông báo nhắc việc tới KTV',
                value: !!appConfig.notifyBeforeDeadline ? 'Sau ' + Utils.formatNumber(appConfig.notifyBeforeDeadline) + ' phút' : '',
                onClick: () => {
                    handleEditSetting(
                        'notifyBeforeDeadline',
                        'Thời gian gửi thông báo nhắc việc tới KTV',
                        'Sau',
                        'Phút',
                        false,
                    )
                },
            },
        ]);
    }, [appConfig]);

    useEffect(() => {
        setTotal(settingList.length);
    }, [settingList]);

    const columns = useMemo(() => [
        {
            id: 'NAME',
            name: UIHelper.buildTableColumn('Tên tác vụ'),
            selector: (row) => (
                <div className='p-1'>{row.name}</div>
            ),
            minWidth: '500px',
        },
        {
            id: 'CONTENT',
            name: UIHelper.buildTableColumn('Nội dung'),
            selector: (row) => (
                <div className='p-1'>{row.value}</div>
            ),
            width: '300px',
        },
        {
            name: UIHelper.buildTableColumn('Thao tác'),
            width: '100px',
            style: {
                position: 'sticky',
                right: 0,
                backgroundColor: '#fff',
                borderLeft: '1px solid #ebedfe',
            },
            cell: (row) => (
                <MenuActionDropdown
                    menuAction={[
                        {
                            iconElement: (
                                <i
                                    className="fa-regular fa-pen-to-square"
                                    style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                ></i>
                            ),
                            text: 'Sửa tác vụ',
                            click: () => {
                                row.onClick()
                            },
                            show: true,
                        },
                    ]}
                />
            ),
        },
    ], [settingList]);

    // MARK: ---- METHODS ----
    const handleRefresh = () => {
        dispatch(thunkGetAppConfig());
    }
    const handleEditSetting = (key, title, label, unit, isDropdown, isRequired) => {
        AppCustomDialogHelper.show(
            'Sửa tác vụ',
            <DialogEditSettingView
                item={appConfig}
                name={key}
                title={title}
                label={label}
                unit={unit}
                isDropdown={isDropdown}
                isRequired={isRequired}
                hasManyInput={Array.isArray(appConfig[key])}
                onCancel={() => AppCustomDialogHelper.hide()}
                onDone={() => {
                    AppCustomDialogHelper.hide();
                    handleRefresh();
                }}
            />,
            'md',
            false,
            true,
        )
    }

    return (
        <div>
            {UIHelper.buildCard(
                <div>
                    <div className="pb-4">
                        <div className="px-4 pt-4 pb-4 d-flex align-items-center justify-content-between">
                            <div className="d-flex flex-column">
                                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                                    Cài đặt chung
                                </span>
                                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                                    Tổng:{' '}
                                    <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                                        {Utils.formatNumber(total)} bản ghi
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div>
                            <div className="px-4">
                                <KTTable
                                    columns={columns}
                                    items={settingList}
                                    selectableRows={false}
                                    striped={false}
                                    compactGrid={false}
                                    onClickedRow={() => { }}
                                    onSelectedRowsChanged={() => { }}
                                    onDoubleClickedRow={() => { }}
                                    noDataComponent={UIHelper.buildEmptyNoData(
                                        'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GeneralSettingView;
